import { useState, useEffect } from "react";
import { authClient } from "@/lib/auth-client";

type Stats = {
	totalUsers: number;
	adminUsers: number;
	bannedUsers: number;
	recentUsers: { name: string; email: string; createdAt: Date | string; role: string | null }[];
};

type AdminUser = {
	id: string;
	name: string;
	email: string;
	createdAt: Date | string;
	role: string | null;
	banned: boolean | null;
};

type ListResponse = {
	users: AdminUser[];
	total: number;
};

function StatCard({
	label,
	value,
	color,
}: {
	label: string;
	value: number | string;
	color: string;
}) {
	return (
		<div
			style={{
				flex: 1,
				padding: "0.85rem",
				borderRadius: "var(--radius-md)",
				border: "1px solid var(--color-border)",
				background: "var(--color-surface)",
				textAlign: "center",
			}}
		>
			<div
				style={{
					fontSize: "1.5rem",
					fontWeight: 800,
					color,
					lineHeight: 1,
				}}
			>
				{value}
			</div>
			<div
				style={{
					fontSize: "0.72rem",
					color: "var(--color-muted)",
					marginTop: "0.3rem",
					fontWeight: 500,
				}}
			>
				{label}
			</div>
		</div>
	);
}

export default function AdminDashboard() {
	const [stats, setStats] = useState<Stats | null>(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		(async () => {
			try {
				const allUsers: AdminUser[] = [];
				let offset = 0;
				const batchSize = 100;
				let serverTotal = 0;
				while (true) {
					const res = await authClient.admin.listUsers({
						query: { limit: batchSize, offset },
					});
					if (res.error) return;
					const data = res.data as ListResponse;
					serverTotal = data.total;
					const batch = data.users ?? [];
					allUsers.push(...batch);
					offset += batch.length;
					if (offset >= serverTotal || batch.length === 0) break;
				}
				setStats({
					totalUsers: serverTotal,
					adminUsers: allUsers.filter((u) => u.role === "admin").length,
					bannedUsers: allUsers.filter((u) => u.banned).length,
					recentUsers: allUsers
						.sort(
							(a, b) =>
								new Date(b.createdAt).getTime() -
								new Date(a.createdAt).getTime(),
						)
						.slice(0, 5),
				});
			} catch {
				/* ignore */
			} finally {
				setLoading(false);
			}
		})();
	}, []);

	if (loading) {
		return (
			<div style={{ padding: "1rem 0", textAlign: "center", color: "var(--color-muted)", fontSize: "0.85rem" }}>
				Memuat...
			</div>
		);
	}

	if (!stats) {
		return (
			<div style={{ padding: "1rem 0", textAlign: "center", color: "var(--color-muted)", fontSize: "0.85rem" }}>
				Gagal memuat data
			</div>
		);
	}

	return (
		<div>
			<div style={{ display: "flex", gap: "0.6rem", marginBottom: "1.25rem" }}>
				<StatCard label="Total User" value={stats.totalUsers} color="var(--color-primary)" />
				<StatCard label="Admin" value={stats.adminUsers} color="#16a34a" />
				<StatCard label="Banned" value={stats.bannedUsers} color="#dc2626" />
			</div>

			<h3 style={{ fontSize: "0.9rem", fontWeight: 700, margin: "0 0 0.75rem" }}>
				User Terbaru
			</h3>
			<div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
				{stats.recentUsers.map((u, i) => (
					<div
						key={i}
						style={{
							display: "flex",
							alignItems: "center",
							gap: "0.65rem",
							padding: "0.6rem 0.75rem",
							borderRadius: "var(--radius-md)",
							border: "1px solid var(--color-border)",
							background: "var(--color-surface)",
						}}
					>
						<div
							style={{
								width: 32,
								height: 32,
								borderRadius: "50%",
								background: "var(--color-primary-soft)",
								color: "var(--color-primary)",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								fontWeight: 700,
								fontSize: "0.8rem",
								flexShrink: 0,
							}}
						>
							{u.name.charAt(0).toUpperCase()}
						</div>
						<div style={{ flex: 1, minWidth: 0 }}>
							<div
								style={{
									fontSize: "0.82rem",
									fontWeight: 600,
									whiteSpace: "nowrap",
									overflow: "hidden",
									textOverflow: "ellipsis",
								}}
							>
								{u.name}
							</div>
							<div
								style={{
									fontSize: "0.72rem",
									color: "var(--color-muted)",
									whiteSpace: "nowrap",
									overflow: "hidden",
									textOverflow: "ellipsis",
								}}
							>
								{u.email}
							</div>
						</div>
						{u.role === "admin" && (
							<span
								style={{
									padding: "0.1rem 0.45rem",
									borderRadius: "9999px",
									fontSize: "0.65rem",
									fontWeight: 600,
									background: "var(--color-primary)",
									color: "#fff",
								}}
							>
								admin
							</span>
						)}
					</div>
				))}
			</div>
		</div>
	);
}
